import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { AlertService } from '@out-web/core';
import { AlertComponent } from '@out-web/components';
import { HttpClient } from '@angular/common/http';

interface RequestType {
  id: string;
  label: string;
  emailText: string;
  desc: string;
  paid: boolean;
}

@Component({
  selector: 'out-edit-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxDropzoneModule,
    RouterLink,
    AlertComponent,
  ],
  templateUrl: './edit-request.component.html',
  styleUrl: './edit-request.component.scss',
})
export class EditRequestComponent {
  requestForm: FormGroup;
  selectedRequests: Set<string> = new Set();
  isSubmitting = false;
  files: File[] = [];
  listingId: string | null = null;
  listingModel: string | null = null;
  listingYear: string | null = null;
  listingTrim: string | null = null;
  listingColour: string | null = null;

  requestTypes: RequestType[] = [
    {
      id: 'Update Listing Features',
      label: 'Update Listing Features',
      emailText: 'Update listing features',
      desc: 'Modify other details of your listing (state changes in the description)',
      paid: false,
    },
    {
      id: 'Price Change',
      label: 'Price Change',
      emailText: 'Change of listing price',
      desc: 'This request is for changing the price of your vehicle',
      paid: false,
    },
    {
      id: 'Images Change',
      label: 'Images Change',
      emailText: 'Update listing images',
      desc: 'Update images of your listing',
      paid: false,
    },
    {
      id: 'Additional Services Change',
      label: 'Purchase Additional Services',
      emailText: 'Purchase additional services',
      desc: 'Buy extra ad services to reach more customers.',
      paid: true,
    },
    {
      id: 'Date Change - making it appear as newly created and placing it at the top.',
      label: 'Listing Refresh',
      emailText: 'Change of listing date',
      desc: "This updates the listing's creation date, making it appear as newly created and placing it at the top.",
      paid: true,
    },
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    public auth: AuthService,
    private alertService: AlertService,
    private http: HttpClient
  ) {
    this.requestForm = this.fb.group({
      description: ['', [Validators.required, Validators.minLength(10)]],
    });
  }

  ngOnInit(): void {
    this.setRouteParams();
  }

  private setRouteParams() {
    this.route.paramMap.subscribe((params) => {
      this.listingId = params.get('id');
      this.listingModel = params.get('model');
      this.listingYear = params.get('year');
      this.listingTrim = params.get('trim');
      this.listingColour = params.get('colour');
    });
  }

  toggleRequest(requestId: string) {
    this.selectedRequests.clear();
    this.selectedRequests.add(requestId);
    this.updateSubject();
  }

  private updateSubject() {
    if (this.selectedRequests.size === 0) {
      this.requestForm.patchValue({ subject: '' });
      return;
    }

    const selectedLabel = this.requestTypes.find((type) =>
      this.selectedRequests.has(type.id)
    )?.emailText;

    this.requestForm.patchValue({
      subject: selectedLabel ? `Request for: ${selectedLabel}` : '',
    });
  }

  async onSubmit() {
    if (this.requestForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      const requestData = {
        subject: 'A Customer is Requesting Changes their Listing',
        description: this.requestForm.get('description')?.value,
        listing_short_id: this.listingId || '',
        user_email: (await firstValueFrom(this.auth.user$))?.email || '',
      };

      await firstValueFrom(
        this.http.post(
          'https://price-track.azurewebsites.net/api/sendOUTMainRequestEmail',
          requestData,
          {
            headers: { 'Content-Type': 'application/json' },
          }
        )
      );

      this.Alert('success', 'Success', 'Form submitted successfully');
      this.requestForm.reset();
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      this.isSubmitting = false;
    }
  }

  Alert(
    alertType: 'error' | 'success' | 'info' | 'warning' | 'white',
    alertTitle: string,
    alertMessage: string
  ) {
    this.alertService.showAlert({
      title: alertTitle,
      message: alertMessage,
      type: alertType,
      duration: 8000,
      position: 'top-right',
    });
  }
}
